/**
 * R6 §8.1 — the global header. ⭐ **AFTER R9 IT IS DELIBERATELY NOT NAVIGATION.**
 *
 * It answers *which system am I pointed at* and *who am I*, and nothing else: brand · `ml-auto` ·
 * `PolarityToggle` · `UserMenu`. **Every destination lives in the rail** (`NavRail`) — *"one navigation
 * surface, not three."*
 *
 * ## ⛔ WHAT WAS DELETED FROM HERE, AND WHY NEITHER IS RECOVERABLE BY "SIMPLIFYING IT BACK"
 *
 * **1. The section strip** (`section-strip-nav` / `section-strip-static`, `SectionTab`,
 * `sectionAriaCurrent`). Sections are now rail GROUPS (R9 N-1/N-2), so a top-bar strip of the same five
 * words is the second of the three surfaces the owner complained about. ⚠️ Its `Link`-not-`NavLink`
 * ruling and its M4 `aria-current` values were *correct* and are simply no longer needed — a rail
 * link's `to` IS its screen, so `NavLink`'s own match is the right answer there, which is why `NavRail`
 * uses it and this file did not.
 *
 * ⛔ **The four properties those assertions guarded did NOT go away with the markup.** *"Exactly one
 * lit nav item"*, *"an inactive item emits NO `aria-current` at all"*, *"a sibling screen keeps its
 * group lit"* and *"nothing is lit when nothing resolves"* are rehomed onto the rail, document-scoped
 * and STRONGER — the old ones were `within(nav)`, which is precisely why none of them could see the
 * three-landmark tripling M4 later fixed. See `chrome.test.tsx`'s `NavRail` describe (R9 N-8 item 3).
 *
 * **2. The app chip** (`header-app-chip`). It moved INTO the rail as the scope control — *"the app
 * context chip is a CONTROL, not a label: it is what the rail below it is scoped to"* — so it now sits
 * adjacent to the thing it changes. ⛔ Two chips naming the same app in two places is exactly the
 * duplication being removed; do not re-add one here.
 *
 * ⛔ **`ml-auto` STAYS ON AN ALWAYS-RENDERED WRAPPER, and this survived the edit deliberately.** It
 * must not move onto the session block: the theme control would then be session-gated, and the
 * zero-access frame — whose header is passed NO session (`AuthenticatedShell.frame()`) — would lose the
 * only way to switch theme. That population has nothing else on screen; denying them a theme is the one
 * case where it is most visible. ⚠️ With the strip and the chip gone the wrapper is the header's ONLY
 * flexible child, so `ml-auto` looks redundant and is not: without it the cluster sits beside the brand.
 */
import { Shield } from 'lucide-react';
import { useCpBranding } from '../branding/CpBrandingContext';
import { PolarityToggle } from '../theme/PolarityToggle';
import { UserMenu } from './UserMenu';

/**
 * R2/N3 — who is signed in, and how they get out.
 *
 * ⛔ Threaded as a PROP rather than read from `useAuth()` here. Calling that hook a second time
 * would create a SECOND, independent auth state machine — it is `useState`-backed, not a
 * context — so this header would sign out a session the rest of the tree still believes is live.
 */
export interface ShellSession {
  readonly email: string | null;
  readonly onSignOut: () => void;
}

export interface SectionHeaderProps {
  /**
   * ⛔ Required-and-nullable rather than optional. `SectionLayout` is the only caller and it
   * always has an answer, so an `= null` default would be a permanently un-taken branch — an
   * un-coverable line dressed up as ergonomics.
   */
  readonly session: ShellSession | null;
}

export function SectionHeader({ session }: SectionHeaderProps): React.JSX.Element {
  const brand = useCpBranding();
  const name = brand?.consoleName ?? brand?.productName ?? 'Console';
  const logo = brand?.logo?.darkUrl ?? brand?.logo?.lightUrl ?? null;

  return (
    <header
      className="flex h-14 shrink-0 items-center gap-6 border-b border-border-subtle bg-chrome px-6"
      data-testid="section-header"
    >
      <div className="flex items-center gap-2.5" data-testid="section-header-brand">
        {logo ? (
          <img src={logo} alt={name} className="h-5 w-auto" />
        ) : (
          <Shield className="h-5 w-5 text-accent" aria-hidden />
        )}
        <span className="text-sm font-bold tracking-tight text-primary">{name}</span>
      </div>

      {/*
        The right-hand cluster. ⛔ `ml-auto` lives HERE, on a wrapper that ALWAYS renders, not on the
        session block — because the theme control must not be session-gated. It was the session block
        that carried `ml-auto`, so adding the toggle inside it (the natural place, right next to the
        other right-aligned chrome) would have hidden the only way to switch theme from the
        zero-access frame, whose header is passed no session at all
        (`AuthenticatedShell.frame()`). That population has nothing else on screen; denying them a
        theme is the one case where it is most visible.

        ⚠️ Order matches the widget the owner pointed at: the DP shell puts the toggle FIRST in its
        right cluster and the profile after it (the DP shell's `Header` component).
        It also keeps sign-out the rightmost control, so a utility press cannot land on it.
      */}
      <div className="ml-auto flex items-center gap-3">
        <PolarityToggle />
        {/*
          ⭐ THE USER MENU — the owner's *"we need the whole header from DP, i.e. the user dropdown in
          the header with some of the settings under that."*

          ⛔ This REPLACED a bare email label plus a top-level `Sign out` button. Sign out now lives
          inside the menu, exactly where the DP header puts it, and keeps its `section-header-sign-out`
          test id so the five suites that drive it name the same control. `UserMenu`'s own header
          carries the full DP-item enumeration and the reason for every difference.

          ⚠️ The `section-header-session` marker moved ONTO the menu's container, so "no session ⇒ no
          identity region" is still one query, and it is still absent on the zero-access frame.

          ⛔ `PolarityToggle` STAYS OUTSIDE IT. Platform-11 is *"add the widget the DP shell already
          has in its header"*, DP's own menu carries no theme control, and this component's right
          cluster is ungated precisely so the zero-access frame — which gets no session — still has
          one. See `UserMenu`'s header and `theme/PolarityToggle.tsx`.
        */}
        {session === null ? null : (
          <UserMenu email={session.email} onSignOut={session.onSignOut} />
        )}
      </div>
    </header>
  );
}
